import RequirePalmettoFinanceUser from '@/components/auth/RequirePalmettoFinanceUser';
import RequirePermissions from '@/components/auth/requirePermissions';
import { useGetWarrantyContractSkusQuery, useSetWarrantyContractSkuStatusMutation } from '@/services/warranties';
import { Badge, Box, Button, Card, Column, Table, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { UserPermissions, WarrantyContractSkuStatus } from 'types';

import DateTimeDisplay from '../../DateTime';

import { ContractSkuModal } from './ContractSkuModal';

interface SkuRow {
  id: string;
  warrantyProvider: string;
  programType: string;
  term: number;
  contractSku: string;
  payoutAmount?: number;
  displayName?: string;
  description?: string;
  status: WarrantyContractSkuStatus;
  createdAt: string;
}

export const ContractSkus = () => {
  const { programType } = useParams<{ programType: string }>();
  const {
    data: skus = [],
    isLoading,
    isFetching,
  } = useGetWarrantyContractSkusQuery(programType ? { programType } : {});
  const [setSkuStatus] = useSetWarrantyContractSkuStatusMutation();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();

  const handleToggleStatus = useCallback(
    async (sku: SkuRow) => {
      const isActive = sku.status === WarrantyContractSkuStatus.active;
      const newStatus = isActive ? WarrantyContractSkuStatus.discontinued : WarrantyContractSkuStatus.active;
      const confirmed = window.confirm(
        isActive
          ? 'Are you sure you want to discontinue this contract SKU?'
          : 'Are you sure you want to re-enable this contract SKU?',
      );
      if (!confirmed) return;
      try {
        await setSkuStatus({ id: sku.id, status: newStatus }).unwrap();
        toast.success(isActive ? 'Contract SKU discontinued' : 'Contract SKU re-enabled');
      } catch {
        toast.error(isActive ? 'Failed to discontinue contract SKU' : 'Failed to re-enable contract SKU');
      }
    },
    [setSkuStatus],
  );

  const columns: Column[] = [
    { heading: 'Display Name', dataKey: 'displayName' },
    { heading: 'SKU', dataKey: 'contractSku' },
    { heading: 'Provider', dataKey: 'warrantyProvider' },
    { heading: 'Term (yrs)', dataKey: 'term', width: 100 },
    {
      heading: 'Reimbursement Rate ($)',
      dataKey: 'payoutAmount',
      width: 130,
      render: (cell: any) => (cell != null ? `$${Number(cell).toLocaleString()}` : '—'),
    },
    { heading: 'Description', dataKey: 'description' },
    {
      heading: 'Status',
      dataKey: 'status',
      width: 140,
      render: (_cell: any, row: any) => (
        <Badge
          variant={(row as SkuRow).status === WarrantyContractSkuStatus.active ? 'success' : 'warning'}
          message={(row as SkuRow).status === WarrantyContractSkuStatus.active ? 'Active' : 'Discontinued'}
        />
      ),
    },
    {
      heading: 'Created',
      dataKey: 'createdAt',
      render: (cell: any) => <DateTimeDisplay value={cell} dateFormat="DD" excludeTime />,
    },
    {
      heading: '',
      dataKey: 'id',
      width: 80,
      render: (_cell: any, row: any) => {
        const typedRow = row as SkuRow;
        const isActive = typedRow.status === WarrantyContractSkuStatus.active;
        return (
          <Button
            onClick={() => handleToggleStatus(typedRow)}
            iconPrefix={isActive ? 'ban' : 'check'}
            size="sm"
            variant="tertiary"
            tone="neutral"
            aria-label={isActive ? 'Discontinue SKU' : 'Re-enable SKU'}
          />
        );
      },
    },
  ];

  return (
    <>
      <ContractSkuModal isOpen={isModalOpen} onClose={closeModal} />
      <Card padding="sm">
        <Card.Header>
          <Box direction="row" justifyContent="space-between">
            <Box fontSize="md" fontWeight="medium">
              Warranty Contracts
            </Box>
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
                <Button
                  as="button"
                  variant="primary"
                  iconPrefix="add"
                  size="sm"
                  aria-label="add contract SKU"
                  onClick={openModal}
                >
                  SKU
                </Button>
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </Box>
        </Card.Header>
        <Table
          rowKey="id"
          columns={columns}
          rows={skus}
          isLoading={isLoading || isFetching}
          isScrollable={{ x: true }}
        />
      </Card>
    </>
  );
};
